import { useRequest } from 'features/request/useRequest';
import type {
    WorkOrderChangeOtherPriceIdsInput,
    WorkOrderChangePriceIdsInput,
    WorkOrderChangePriceItem,
} from 'gql/graphql';
import { WorkOrderPriceTypeEnum } from 'gql/graphql';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { uerRemoveWorkOrderChangePriceItemsGql } from '../../WorkOrderChangeQueries';

export const useDeleteFromWorkOrderChange = ({
    workOrderChangeId,
    constructionTypeId,
    checkedRows,
    singleDeleteRow,
}: {
    workOrderChangeId: number;
    constructionTypeId: number;
    checkedRows: Record<string, WorkOrderChangePriceItem | undefined>;
    singleDeleteRow?: WorkOrderChangePriceItem;
}) => {
    const dispatch = useDispatch();

    const [data, status, err, request, setData] = useRequest({
        requestId: 'deleteFromWorkOrderChange',
        query: uerRemoveWorkOrderChangePriceItemsGql,
        withEffect: false,
    });

    const variables = useMemo(() => {
        const singleDeleteIds: {
            priceIds: WorkOrderChangePriceIdsInput[];
            otherPriceIds: WorkOrderChangeOtherPriceIdsInput[];
        } =
            singleDeleteRow?.type === WorkOrderPriceTypeEnum.Other
                ? {
                      priceIds: [],
                      otherPriceIds: [
                          {
                              workOrderChangeOtherPriceId: singleDeleteRow?.workOrderChangePriceId,
                              workOrderOtherPriceId: singleDeleteRow?.workOrderPriceId,
                          },
                      ],
                  }
                : {
                      priceIds: [
                          {
                              workOrderChangePriceId: singleDeleteRow?.workOrderChangePriceId,
                              workOrderPriceId: singleDeleteRow?.workOrderPriceId,
                          },
                      ],
                      otherPriceIds: [],
                  };

        const { priceIds, otherPriceIds } = Object.values(checkedRows ?? {}).reduce(
            (
                acc: {
                    priceIds: WorkOrderChangePriceIdsInput[];
                    otherPriceIds: WorkOrderChangeOtherPriceIdsInput[];
                },
                row,
            ) => {
                if (row) {
                    if (row.type === WorkOrderPriceTypeEnum.Other) {
                        acc.otherPriceIds.push({
                            workOrderChangeOtherPriceId: row.workOrderChangePriceId,
                            workOrderOtherPriceId: row.workOrderPriceId,
                        });
                    } else {
                        acc.priceIds.push({
                            workOrderChangePriceId: row.workOrderChangePriceId,
                            workOrderPriceId: row.workOrderPriceId,
                        });
                    }
                }

                return acc;
            },
            { priceIds: [], otherPriceIds: [] },
        );

        if (singleDeleteRow) {
            return {
                workOrderChangeId,
                constructionTypeId,
                ...singleDeleteIds,
            };
        }

        return {
            workOrderChangeId,
            constructionTypeId,
            priceIds,
            otherPriceIds,
        };
    }, [checkedRows, constructionTypeId, singleDeleteRow, workOrderChangeId]);

    const handleDelete = useCallback(() => {
        dispatch(request(variables));
    }, [dispatch, request, variables]);

    const objectIdsArrDeleteMutations = useMemo(() => {
        if (singleDeleteRow) {
            return [singleDeleteRow?.workOrderObjectId];
        }

        return Object.values(checkedRows ?? {})
            .filter(Boolean)
            .map((row) => row?.workOrderObjectId);
    }, [checkedRows, singleDeleteRow]);

    return {
        deleteData: data?.uerRemoveWorkOrderChangePriceItems,
        deleteErr: err,
        deleteSetData: setData,
        deleteStatus: status,
        handleDelete,
        objectIdsArrDeleteMutations,
    };
};
